import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { fetchWithAuthRejectAllErrors } from '../../shared/fetch-json.util';
import { getEnvVar } from '../../utils/getEnvVar';
import {
  PHASE_DATES_UPDATE_FAILURE,
  PHASE_UPDATE_FAILURE,
  SAVE_SUCCESSFUL
} from '../../shared/AppConstants';

const initialState = {
  phaseData: {},
  updatedPhaseData: {},
  activePhase: undefined,
  strategyNumber: undefined,
  updatedPhase: undefined,
  modalErrorMessage: undefined,
  modalSuccessMessage: undefined,
  savePhaseDatesSuccess: false,
  saveStrategySuccess: false
};

export const fetchPhaseData = createAsyncThunk(
  'phases/fetchPhaseData',
  async ({ strategyNumber }) => {
    const url = `${getEnvVar('VITE_GSS_API_URL')}/${strategyNumber}/completionDates`;
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    };

    const response = await fetchWithAuthRejectAllErrors(url, config);

    return response.json();
  }
);

export const fetchSaveStrategy = createAsyncThunk(
  'phases/fetchSaveStrategy',
  async (_, { getState }) => {
    if (getState().phases.activePhase === getState().phases.updatedPhase) {
      return;
    }

    const strategyNumber = getState().phases.strategyNumber;
    const url = `${getEnvVar('VITE_GSS_API_URL')}/new/${strategyNumber}`;
    const payload = {
      currentPhase: Number(getState().phases.updatedPhase)
    };

    const config = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    };

    const response = await fetchWithAuthRejectAllErrors(url, config);

    return response.json();
  }
);

const clearModalMessage = (state) => {
  state.modalErrorMessage = undefined;
  state.modalSuccessMessage = undefined;
  state.savePhaseDatesSuccess = undefined;
  state.saveStrategySuccess = undefined;
};

export const fetchSavePhaseDates = createAsyncThunk(
  'phases/fetchSavePhaseDates',
  async (_, { getState }) => {
    const strategyNumber = getState().phases.strategyNumber;
    const url = `${getEnvVar('VITE_GSS_API_URL')}/${strategyNumber}/completionDates/bulk`;

    const updatedPhaseData = getState().phases.updatedPhaseData;
    const currentPhaseData = getState().phases.phaseData;
    const combinedData = [];

    for (const phaseId in updatedPhaseData) {
      if (
        updatedPhaseData[phaseId]?.actualCompletionDate !==
          currentPhaseData[phaseId]?.actualCompletionDate ||
        updatedPhaseData[phaseId]?.estimatedCompletionDate !==
          currentPhaseData[phaseId]?.estimatedCompletionDate
      ) {
        combinedData.push({
          ...(currentPhaseData[phaseId] || {}),
          ...updatedPhaseData[phaseId]
        });
      }
    }

    if (combinedData.length === 0) {
      return;
    }

    const body = combinedData.map((phase) => ({
      phaseId: phase.phaseId,
      estimatedCompletionDate: phase.estimatedCompletionDate,
      actualCompletionDate: phase.actualCompletionDate,
      completionDateId: phase.completionDateId
    }));

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    };

    const response = await fetchWithAuthRejectAllErrors(url, config);

    return response.json();
  }
);

const phasesSlice = createSlice({
  name: 'phasesSlice',
  initialState,
  reducers: {
    setStrategyData: (state, action) => {
      const { activePhase, strategyNumber } = action.payload;

      state.activePhase = activePhase;
      state.updatedPhase = activePhase;
      state.strategyNumber = strategyNumber;
    },
    updatePhaseData: (state, action) => {
      const { phaseId } = action.payload;

      state.updatedPhaseData[phaseId] = {
        ...state.updatedPhaseData[phaseId],
        ...action.payload
      };

      clearModalMessage(state);
    },
    setUpdatedPhase: (state, action) => {
      state.updatedPhase = Number(action.payload);
      clearModalMessage(state);
    },
    clearModalMessages: (state) => clearModalMessage(state),
    setModalSuccessMessage: (state) => {
      state.modalSuccessMessage = SAVE_SUCCESSFUL;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSaveStrategy.fulfilled, (state, action) => {
        state.saveStrategySuccess = true;
        state.modalSuccessMessage = SAVE_SUCCESSFUL;
        if (!action.payload) {
          return;
        }
        state.activePhase = action.payload.currentPhase;
      })
      .addCase(fetchSaveStrategy.rejected, (state) => {
        state.modalErrorMessage = PHASE_UPDATE_FAILURE;
      })
      .addCase(fetchSavePhaseDates.rejected, (state) => {
        state.modalErrorMessage = PHASE_DATES_UPDATE_FAILURE;
      })
      .addCase(fetchSavePhaseDates.fulfilled, (state, action) => {
        state.savePhaseDatesSuccess = true;
        state.modalSuccessMessage = SAVE_SUCCESSFUL;
        if (!action.payload) {
          return;
        }
        action.payload.forEach((phase) => {
          state.phaseData[phase.phaseId] = phase;
        });
        state.updatedPhaseData = {};
      })
      .addCase(fetchPhaseData.fulfilled, (state, action) => {
        action.payload.forEach((phase) => {
          state.phaseData[phase.phaseId] = phase;
        });
        state.updatedPhaseData = {};
      })
      .addMatcher(
        isAnyOf(fetchSavePhaseDates.pending, fetchSaveStrategy.pending),
        (state) => {
          state.modalErrorMessage = undefined;
          state.modalSuccessMessage = undefined;
          clearModalMessage(state);
        }
      );
  }
});

export const selectPhaseData = (phaseId) => (state) =>
  state.phases.phaseData?.[phaseId];

export const selectUpdatedPhaseData = (phaseId) => (state) =>
  state.phases.updatedPhaseData?.[phaseId];

export const selectModalSuccessMessage = (state) => {
  return (
    state.phases.savePhaseDatesSuccess &&
    state.phases.saveStrategySuccess &&
    state.phases.modalSuccessMessage
  );
};

export const {
  setStrategyData,
  updatePhaseData,
  setUpdatedPhase,
  clearModalMessages,
  setModalSuccessMessage
} = phasesSlice.actions;

export const phasesReducer = phasesSlice.reducer;
